import { Recording, Subject } from "@/models/recording";
import { formatMsToTime } from "../formatTime";
import { getDeviceKey } from "@/storage/authentication";
import { getUploadURL } from "@/services/getUploadURL";
import { ChunkData, getRecordingDataFromDB, RecordingData } from "../indexedDBUtils";
import { SubjectOffsetPair } from "../updateRecordingSubjects";
import { getRecordingTranscription } from "@/services/getRecordingTranscription";
import { AxiosError } from "axios";
import { getAttendanceInitials } from "../getAttendanceInitials";
import { fetchRecordingAgendaItems } from "@/hooks/useRecordingAgendaItems";
import { fetchAgendaDetails } from "@/hooks/useAgendaItems";

export const isRecordingPaused = (record: Recording, allRecordingData: RecordingData[]): boolean => {
  const hasPausedStatus = record.status === 7;
  const recordingData = allRecordingData.find((r) => r.recordingUUID === record.recordingId);
  const isFailedAndPaused = recordingData?.status === 7;
  return hasPausedStatus || Boolean(isFailedAndPaused);
};

export const handleStartSubjects = (subjects: Subject[]) => {
  if (subjects.some((subject) => subject.segments.length)) {
    return subjects;
  }

  const initialDuration = formatMsToTime(0);

  return subjects.map((subject: Subject, index: number) => {
    if (index === 0) {
      return {
        ...subject,
        time: initialDuration,
        segments: [{ startOffSet: initialDuration, endOffSet: "" }],
      };
    }

    return {
      ...subject,
      segments: [],
    };
  });
};

export const toSubjectOffsetPairs = (subjects: Subject[]): SubjectOffsetPair[] => {
  return subjects.map((subject) => ({
    recordingSubjectId: subject.id,
    segments: subject.segments.filter((s) => s.startOffSet && s.endOffSet),
  }));
};

export const finishSubjectAtDuration = (subjects: Subject[], subjectIndex: number, duration: string): Subject[] => {
  return subjects.map((item, index) => {
    if (index !== subjectIndex) {
      return item;
    }

    if (!item.segments.length) {
      return {
        ...item,
        time: duration,
      };
    }

    const lastSegmentIndex = item.segments.length - 1;
    const segments = item.segments.map((segment, segmentIndex) => {
      if (segmentIndex !== lastSegmentIndex) {
        return segment;
      }

      return {
        ...segment,
        endOffSet: duration,
      };
    });

    return {
      ...item,
      time: duration,
      segments,
    };
  });
};

export const orderExpectedChunks = (expectedChunks: string[]): string[] => {
  return [...expectedChunks].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
};

export const hasAllLocalChunksBeenUploaded = (chunks: ChunkData[], orderedExpectedChunks: string[]): boolean => {
  if (!chunks.length || !orderedExpectedChunks.length) {
    return false;
  }

  const remoteChunkIds = new Set(orderedExpectedChunks.map((name) => name.replace(/\.webm$/, "")));
  return chunks.every((chunk) => remoteChunkIds.has(chunk.blockId));
};

// Thrown when a recording no longer has local data to upload against (e.g. it was
// discarded/deleted). Callers should treat this as unrecoverable rather than retry.
export class RecordingNotFoundError extends Error {
  constructor(recordingUUID: string) {
    super(`Upload Chunk: New Recording data False  - ${recordingUUID}`);
    this.name = "RecordingNotFoundError";
  }
}

export async function handleGetUploadURL(recordingUUID: string, saveRecordingData: (updatedRecordingData: RecordingData) => Promise<void>) {
  const newRecordingData = await getRecordingDataFromDB(recordingUUID);
  const deviceKey = getDeviceKey(import.meta.env.VITE_IBABS_AUTH_DEVICE_KEY);
  if (!newRecordingData) {
    throw new RecordingNotFoundError(recordingUUID);
  }
  if (!deviceKey) {
    throw new Error(`Upload Chunk: Device Key is not valid - ${newRecordingData}`);
  }
  const { uploadURL, ...rest } = newRecordingData;

  const urlParams = new URLSearchParams(uploadURL.split("?")[1]);
  const expirationDate = urlParams.get("se");

  if (expirationDate) {
    const urlExpirationTime = new Date(expirationDate).getTime();
    const isURLValid = urlExpirationTime > Date.now();

    if (!isURLValid) {
      const newUploadURL = await getUploadURL(recordingUUID, deviceKey);
      await saveRecordingData({ ...rest, uploadURL: newUploadURL });

      return newUploadURL;
    } else {
      return uploadURL as string;
    }
  }
  throw new Error(`Upload Chunk: Uploading URL invalid - ${uploadURL}`);
}

export function resolveRecordingLanguage(selectedLanguage: string, userLang: string | undefined) {
  return selectedLanguage === "fr" ? "en" : selectedLanguage || userLang || "en";
}

export const recoverRecordingData = async (recordingUUID: string) => {
  const recordingInfo = await getRecordingTranscription({ recordingId: recordingUUID });

  if (!recordingInfo || typeof recordingInfo === "string" || recordingInfo instanceof AxiosError) {
    throw new Error(`Recording data not found for recordingUUID: ${recordingUUID}`);
  }

  const deviceKey = getDeviceKey(import.meta.env.VITE_IBABS_AUTH_DEVICE_KEY);

  if (!deviceKey) {
    throw new Error("Device key not found");
  }

  const uploadUrl = await getUploadURL(recordingUUID, deviceKey);
  const finalUploadURLWithFileExtension = uploadUrl.split(recordingUUID).join(`${recordingUUID}.webm`);

  const subjects = await fetchRecordingAgendaItems(deviceKey, recordingUUID);

  const selectedAgendaData = await fetchAgendaDetails(deviceKey, recordingInfo.agendaId);
  const recordingData: RecordingData = {
    id: `recording-${recordingUUID}-${Date.now()}`,
    meetingId: recordingInfo.agendaId,
    meetingName: recordingInfo.title || "",
    meetingDateTime: recordingInfo.date || "",
    meetingStartDate: selectedAgendaData?.startDate || "",
    meetingEndDate: selectedAgendaData?.endDate || "",
    guestsList: [],
    guestsListInitials: recordingInfo.users.map((attendee) => getAttendanceInitials(attendee.firstName)),
    guestsListFullName: recordingInfo.users.map((attendee) => attendee.firstName),
    selectedLanguage: "nl",
    audioInput: "default",
    recordingUUID: recordingUUID || "",
    uploadURL: finalUploadURLWithFileExtension,
    subjects: subjects || [],
    duration: formatMsToTime(0),
    durationTimestamp: 0,
    timestamp: Date.now(),
    status: recordingInfo.status,
    subjectIndex: 0,
  };

  return recordingData;
};
